import worldsJson from "./generated/worlds.json";
import { globalRepo, K } from "./globalRepo.js";

export const WORLD_CHAPTER_SPAN = globalRepo.getInt(K.worldChapterSpan);

export const WORLD_VISUAL_FAMILIES = ["default", "space", "circuit"] as const;
export type WorldVisualFamily = (typeof WORLD_VISUAL_FAMILIES)[number];

export interface WorldBrickTextures {
  readonly visualFamily: WorldVisualFamily;
  readonly normalBase: string;
  readonly goldBase: string;
  readonly mysteryBase: string;
  readonly overlay: string;
}

export interface WorldDefinition {
  readonly worldId: string;
  readonly worldNo: number;
  readonly iconKey: string;
  readonly bgTileKey: string;
  readonly brickTextures: WorldBrickTextures;
}

interface WorldRow {
  readonly worldId: string;
  readonly worldNo: number;
  readonly iconKey: string;
  readonly bgTileKey: string;
  readonly visualFamily: string;
  readonly brickBaseKey: string;
  readonly brickGoldKey: string;
  readonly brickMysteryKey: string;
  readonly brickOverlayKey: string;
}

function requireNonEmpty(value: string, field: string, worldId: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`worlds.tsv: ${worldId}.${field} is empty`);
  }
  return value;
}

function requireVisualFamily(value: string, worldId: string): WorldVisualFamily {
  const family = requireNonEmpty(value, "visualFamily", worldId);
  if ((WORLD_VISUAL_FAMILIES as readonly string[]).includes(family)) return family as WorldVisualFamily;
  throw new Error(`worlds.tsv: ${worldId}.visualFamily must be one of ${WORLD_VISUAL_FAMILIES.join(", ")}`);
}

function toWorldDefinition(row: WorldRow): WorldDefinition {
  const worldId = requireNonEmpty(row.worldId, "worldId", row.worldId);
  if (!Number.isInteger(row.worldNo) || row.worldNo < 1) {
    throw new Error(`worlds.tsv: ${worldId}.worldNo must be a positive integer`);
  }
  const visualFamily = requireVisualFamily(row.visualFamily, worldId);
  return {
    worldId,
    worldNo: row.worldNo,
    iconKey: requireNonEmpty(row.iconKey, "iconKey", worldId),
    bgTileKey: requireNonEmpty(row.bgTileKey, "bgTileKey", worldId),
    brickTextures: {
      visualFamily,
      normalBase: requireNonEmpty(row.brickBaseKey, "brickBaseKey", worldId),
      goldBase: requireNonEmpty(row.brickGoldKey, "brickGoldKey", worldId),
      mysteryBase: requireNonEmpty(row.brickMysteryKey, "brickMysteryKey", worldId),
      overlay: requireNonEmpty(row.brickOverlayKey, "brickOverlayKey", worldId),
    },
  };
}

function buildWorldDefinitions(rows: readonly WorldRow[]): readonly WorldDefinition[] {
  const worlds = rows.map(toWorldDefinition).sort((a, b) => a.worldNo - b.worldNo);
  if (worlds.length === 0) throw new Error("worlds.tsv: at least one world is required");
  if (worlds[0]!.worldNo !== 1) throw new Error("worlds.tsv: first worldNo must be 1");
  for (let i = 1; i < worlds.length; i += 1) {
    const prev = worlds[i - 1]!;
    const curr = worlds[i]!;
    if (curr.worldNo === prev.worldNo) throw new Error(`worlds.tsv: duplicate worldNo ${curr.worldNo}`);
  }
  return worlds;
}

export const WORLD_DEFS = buildWorldDefinitions(worldsJson as readonly WorldRow[]);

export function resolveWorldForChapter(
  chapter: number,
  chapterSpan: number,
  worlds: readonly WorldDefinition[] = WORLD_DEFS,
): WorldDefinition {
  if (worlds.length === 0) throw new Error("resolveWorldForChapter: worlds must not be empty");
  const safeChapter = Math.max(1, Math.floor(Number.isFinite(chapter) ? chapter : 1));
  const safeSpan = Math.max(1, Math.floor(Number.isFinite(chapterSpan) ? chapterSpan : 1));
  const targetWorldNo = Math.floor((safeChapter - 1) / safeSpan) + 1;
  let selected = worlds[0]!;
  for (const world of worlds) {
    if (world.worldNo <= targetWorldNo) selected = world;
  }
  return selected;
}

export function worldForChapter(chapter: number): WorldDefinition {
  return resolveWorldForChapter(chapter, WORLD_CHAPTER_SPAN, WORLD_DEFS);
}

export function chapterForWorld(
  worldNo: number,
  chapterSpan: number = WORLD_CHAPTER_SPAN,
  worlds: readonly WorldDefinition[] = WORLD_DEFS,
): number {
  if (worlds.length === 0) throw new Error("chapterForWorld: worlds must not be empty");
  const safeSpan = Math.max(1, Math.floor(Number.isFinite(chapterSpan) ? chapterSpan : 1));
  const firstWorldNo = worlds[0]!.worldNo;
  const lastWorldNo = worlds[worlds.length - 1]!.worldNo;
  const requested = Math.floor(Number.isFinite(worldNo) ? worldNo : firstWorldNo);
  const safeWorldNo = Math.min(Math.max(requested, firstWorldNo), lastWorldNo);
  return (safeWorldNo - 1) * safeSpan + 1;
}

export function chapterInWorld(chapter: number, chapterSpan: number = WORLD_CHAPTER_SPAN): number {
  const safeChapter = Math.max(1, Math.floor(Number.isFinite(chapter) ? chapter : 1));
  const safeSpan = Math.max(1, Math.floor(Number.isFinite(chapterSpan) ? chapterSpan : 1));
  return ((safeChapter - 1) % safeSpan) + 1;
}

export function worldStartChapterForChapter(chapter: number, chapterSpan: number = WORLD_CHAPTER_SPAN): number {
  const safeChapter = Math.max(1, Math.floor(Number.isFinite(chapter) ? chapter : 1));
  const safeSpan = Math.max(1, Math.floor(Number.isFinite(chapterSpan) ? chapterSpan : 1));
  return Math.floor((safeChapter - 1) / safeSpan) * safeSpan + 1;
}

export function bestChapterInCurrentWorld(
  currentChapter: number,
  bestChapter: number,
  chapterSpan: number = WORLD_CHAPTER_SPAN,
): number {
  const safeSpan = Math.max(1, Math.floor(Number.isFinite(chapterSpan) ? chapterSpan : 1));
  const start = worldStartChapterForChapter(currentChapter, safeSpan);
  const end = start + safeSpan - 1;
  const safeBest = Math.max(1, Math.floor(Number.isFinite(bestChapter) ? bestChapter : 1));
  const clampedBest = Math.min(Math.max(safeBest, start), end);
  return chapterInWorld(clampedBest, safeSpan);
}
